import React from 'react';
import '@atlaskit/css-reset';
import type { PreloadedQuery } from 'react-relay';
import { AsyncLocationlessDialog } from '@atlassian/jira-board-location-dialogs-locationless/src/async';
// eslint-disable-next-line jira/wrm/no-load-bridge
import { loadBridge } from '@atlassian/jira-common-bridge/src';
import { fg } from '@atlassian/jira-feature-gating';
import SetCustomMark from '@atlassian/jira-providers-spa-apdex-analytics/src/set-custom-mark/index.tsx';
import type { aggBacklogDataQuery$data } from '@atlassian/jira-relay/src/__generated__/aggBacklogDataQuery.graphql';
import type { viewBacklogCustomFiltersQuery } from '@atlassian/jira-relay/src/__generated__/viewBacklogCustomFiltersQuery.graphql';
import type { viewBacklogVersionFilterRelayQuery } from '@atlassian/jira-relay/src/__generated__/viewBacklogVersionFilterRelayQuery.graphql';
import type { TenantContext } from '@atlassian/jira-shared-types/src/tenant-context.tsx';
import { NextGenBacklogSkeleton } from '@atlassian/jira-skeletons/src/ui/next-gen-backlog/index.tsx';
import BacklogPage from '@atlassian/jira-software-backlog';
import type { PrefetchedDataGql } from '@atlassian/jira-software-backlog-load-agility-graphql-data/src/types';
import type { JiraPlanDataRestResponse } from '@atlassian/jira-software-backlog-uif-types/src/index.tsx';
import SizeLimitErrorEmptyState from '@atlassian/jira-software-size-limit-error-empty-state/src/ui/index.tsx';
import type { UIFBacklogCachedDataResult } from '@atlassian/jira-software-uif-early-script';
import { SpaStatePageReady } from '@atlassian/jira-spa-state-controller/src/components/main.tsx';
import { ERROR_MESSAGE_BOARD_IS_LOCATIONLESS } from '../../../../../common/constants';
import type { RouteResourceError } from '../../../../../common/types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const bindToOnJiraGlobalIssueCreateClose = (callback: any) => {
	// window.require is not available on initial script load.
	// eslint-disable-next-line jira/wrm/no-load-bridge
	loadBridge({
		name: 'jira/util/events',
		wrmKeys: 'wrc!jira.webresources:jira-events',
	}).then((Events) => {
		// @ts-expect-error - TS2571 - Object is of type 'unknown'.
		Events?.bind?.('QuickCreateIssue.sessionComplete', callback);
	});
};

const isBoardSizeLimit = (planDataResponse: JiraPlanDataRestResponse): boolean =>
	planDataResponse.isIssueLimitExceeded;

export type BacklogAppProps = {
	rapidViewId: number;
	tenantContext: TenantContext;
	data: PrefetchedDataGql | null;
	aggData: aggBacklogDataQuery$data | null;
	/**
	 * @deprecated Remove when clean up indexeddb_cache_for_backlog
	 */
	planModeData?: JiraPlanDataRestResponse | null;
	/**
	 * @deprecated Remove when clean up indexeddb_cache_for_backlog
	 */
	planModeDataPromise?: Promise<JiraPlanDataRestResponse | null> | null;
	cmpBacklogData?: UIFBacklogCachedDataResult | null;
	cmpBacklogDataPromise?: Promise<UIFBacklogCachedDataResult | null> | null;
	projectKeyFromContext: string;
	projectIdFromContext: string;
	error: RouteResourceError;
	loading: Boolean;
	isCacheHit: boolean;
	customFiltersQueryRef: PreloadedQuery<viewBacklogCustomFiltersQuery> | null;
	versionFilterRelayQueryRef?: PreloadedQuery<viewBacklogVersionFilterRelayQuery> | null;
	getDataFromAGG?: () => Promise<aggBacklogDataQuery$data>;
};

const BacklogApp = ({
	rapidViewId,
	projectKeyFromContext,
	projectIdFromContext,
	tenantContext,
	data,
	aggData,
	planModeData,
	planModeDataPromise,
	cmpBacklogData,
	cmpBacklogDataPromise,
	isCacheHit,
	customFiltersQueryRef,
	versionFilterRelayQueryRef,
	getDataFromAGG,
	error,
	loading,
}: BacklogAppProps) => {
	// We are assuming that if we get a 400 error, it's because the user doesn't have access to the project for whatever reason
	// including project being deleted that is linked to the board in which we'd prompt the user to change the board's location.
	if (!loading && error && error.message === ERROR_MESSAGE_BOARD_IS_LOCATIONLESS) {
		return <AsyncLocationlessDialog rapidViewId={rapidViewId} />;
	}

	const shouldRenderSkeleton = fg('indexeddb_cache_for_backlog')
		? !cmpBacklogDataPromise
		: !planModeDataPromise;
	if (shouldRenderSkeleton) {
		return <NextGenBacklogSkeleton />;
	}

	if (
		fg('indexeddb_cache_for_backlog')
			? cmpBacklogData && isBoardSizeLimit(cmpBacklogData.result)
			: planModeData && isBoardSizeLimit(planModeData)
	) {
		return <SizeLimitErrorEmptyState />;
	}

	return (
		<>
			<SetCustomMark name="configApiReady" />
			<BacklogPage
				isCacheHit={isCacheHit}
				bindToOnJiraGlobalIssueCreateClose={bindToOnJiraGlobalIssueCreateClose}
				// Route match data that could be read inside of the app
				projectKey={projectKeyFromContext}
				projectId={Number(projectIdFromContext)}
				rapidViewId={String(rapidViewId)}
				// Tenant context data that could be read inside of the app
				locale={tenantContext.locale}
				baseUrl={tenantContext.baseUrl}
				xsrfToken={tenantContext.xsrfToken ?? ''}
				cloudId={tenantContext.cloudId ?? ''}
				atlassianAccountId={tenantContext.atlassianAccountId ?? null}
				prefetchedData={data}
				prefetchedAggData={aggData}
				prefetchedJiraPlanRestData={planModeData}
				prefetchedJiraPlanRestDataPromise={planModeDataPromise}
				prefetchCmpBacklogData={cmpBacklogData}
				prefetchCmpBacklogDataPromise={cmpBacklogDataPromise}
				isServer={__SERVER__}
				customFiltersQueryRef={customFiltersQueryRef}
				versionFilterRelayQueryRef={versionFilterRelayQueryRef}
				getDataFromAGG={getDataFromAGG}
				isCompanyManaged
				// TODO: This is wrong
				projectName={projectKeyFromContext}
			/>
			<SpaStatePageReady />
		</>
	);
};

export default BacklogApp;
